/**
 * 고용 이력 조회 Hook (V92 - httpsCallable 전환)
 *
 * V92 변경사항:
 * - Firestore 직접 접근 → getEmploymentHistoriesByEmployee callable 사용
 * - V87 Rules 강화로 인한 직접 접근 차단 대응
 * - onSnapshot → one-time fetch로 변경
 *
 * V36.7: orderBy 제거 (인덱스 오류 방지) → 프론트 정렬로 변경
 */

import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { EmploymentHistory } from '../types/employmentHistory';

// V95: callable 응답 아이템 타입
interface EmploymentHistoryItem {
  id: string;
  employeeId: string;
  employeeName?: string;
  employmentTypeCode: string;
  employmentTypeName?: string;
  companyType?: string;
  startDate: string;
  endDate?: string;
  isCurrent: boolean;
  probationEndDate?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// V95: callable 응답 타입 (employmentHistories + histories 호환)
interface GetEmploymentHistoriesResponse {
  success?: boolean;
  count?: number;
  employmentHistories?: EmploymentHistoryItem[];
  histories?: EmploymentHistoryItem[];
}

/**
 * 직원의 고용 이력 조회 Hook (V92)
 *
 * V92: getEmploymentHistoriesByEmployee callable 사용
 *
 * @param employeeId - 직원 ID
 * @returns 고용 이력 목록, 로딩 상태, 에러, 리페치 함수
 *
 * @example
 * const { histories, isLoading, error, refetch } = useEmploymentHistories('user123');
 */
export function useEmploymentHistories(employeeId: string | undefined) {
  const [histories, setHistories] = useState<EmploymentHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // V92: 데이터 fetch 함수
  const fetchHistories = useCallback(async () => {
    if (!employeeId) {
      setHistories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      const getEmploymentHistoriesByEmployeeFn = httpsCallable<
        { employeeId: string },
        GetEmploymentHistoriesResponse
      >(functions, 'getEmploymentHistoriesByEmployee');

      const result = await getEmploymentHistoriesByEmployeeFn({ employeeId });

      // V95: 방어적 응답 처리 - employmentHistories 우선, histories 폴백
      const responseData = result.data;
      const histories: EmploymentHistoryItem[] =
        responseData.employmentHistories ??
        responseData.histories ??
        [];
      const success = responseData.success !== false; // undefined도 true로 처리

      if (!success && histories.length === 0) {
        throw new Error('고용 이력 조회 실패');
      }

      // V94: 응답을 EmploymentHistory 타입으로 변환 (required 필드에 기본값 제공)
      const data: EmploymentHistory[] = histories.map((h) => ({
        id: h.id,
        employeeId: h.employeeId,
        employeeName: h.employeeName || '',
        employmentTypeCode: h.employmentTypeCode as EmploymentHistory['employmentTypeCode'],
        employmentTypeName: h.employmentTypeName || '',
        companyType: (h.companyType || 'NGLE') as EmploymentHistory['companyType'],
        startDate: h.startDate,
        endDate: h.endDate,
        isCurrent: h.isCurrent,
        probationEndDate: h.probationEndDate,
        createdAt: h.createdAt ? new Date(h.createdAt) : new Date(),
        updatedAt: h.updatedAt ? new Date(h.updatedAt) : new Date(),
        createdBy: h.createdBy,
        updatedBy: h.updatedBy,
      }));

      // 정렬은 backend에서 처리하지만 안전을 위해 프론트에서도 정렬
      data.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

      setHistories(data);
    } catch (err: any) {
      console.error('[V92] useEmploymentHistories Error:', {
        code: err?.code,
        message: err?.message,
        employeeId,
      });
      setError(err as Error);
      setHistories([]);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId]);

  // V92: 초기 로드
  useEffect(() => {
    fetchHistories();
  }, [fetchHistories]);

  return {
    histories,
    isLoading,
    error,
    refetch: fetchHistories, // V92: 수동 리페치 지원
  };
}

/**
 * 현재 고용형태만 조회 (V92)
 *
 * @param employeeId - 직원 ID
 * @returns 현재 고용 이력, 로딩 상태, 에러, 리페치 함수
 *
 * @example
 * const { currentHistory, isLoading, error, refetch } = useCurrentEmploymentHistory('user123');
 */
export function useCurrentEmploymentHistory(employeeId: string | undefined) {
  const { histories, isLoading, error, refetch } = useEmploymentHistories(employeeId);
  const currentHistory = histories.find((h) => h.isCurrent);

  return { currentHistory, isLoading, error, refetch };
}
